import { apiClient } from './api.js';
import { idempotencyKey } from '@reos/api-client';
import { unwrap } from './api-error.js';
import { createIdBatcher } from './request-batch.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Adresar zakazniku (R4 `/klienti`): typovany klient nad `/v1/parties*`.
 * Zadny druhy zdroj klientu - vsechno tece z `@reos/party` (viz zadani).
 */

export type PartyDto = ApiPaths['/v1/parties']['get']['responses']['200']['content']['application/json']['items'][number];
export type PartyContact =
  ApiPaths['/v1/parties/{id}/contacts']['get']['responses']['200']['content']['application/json'][number];
export type CreatePartyBody =
  ApiPaths['/v1/parties']['post']['requestBody']['content']['application/json'];
export type CreatePartyResponse =
  ApiPaths['/v1/parties']['post']['responses']['201']['content']['application/json'];
export type AddContactBody =
  ApiPaths['/v1/parties/{id}/contacts']['post']['requestBody']['content']['application/json'];
export type UpdatePartyBody =
  ApiPaths['/v1/parties/{id}']['patch']['requestBody']['content']['application/json'];
export type UpdateContactBody =
  ApiPaths['/v1/parties/{id}/contacts/{contactId}']['patch']['requestBody']['content']['application/json'];
export type ArchivePartyResult =
  ApiPaths['/v1/parties/{id}']['delete']['responses']['200']['content']['application/json'];
export type AddTaxIdentifierBody =
  ApiPaths['/v1/parties/{id}/tax-identifiers']['post']['requestBody']['content']['application/json'];

export interface ListPartiesParams {
  readonly q?: string | undefined;
  readonly kind?: PartyDto['kind'] | undefined;
  readonly limit?: number | undefined;
  readonly cursor?: string | undefined;
}

export async function listParties(params: ListPartiesParams = {}): Promise<{ items: PartyDto[]; nextCursor: string | null }> {
  return unwrap(
    apiClient.GET('/v1/parties', {
      params: {
        query: {
          limit: params.limit ?? 50,
          ...(params.q !== undefined ? { q: params.q } : {}),
          ...(params.kind !== undefined ? { kind: params.kind } : {}),
          ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
        },
      },
    }),
  );
}

export async function getParty(id: string): Promise<PartyDto> {
  return unwrap(apiClient.GET('/v1/parties/{id}', { params: { path: { id } } }));
}

/**
 * Krok APP2 bod 1 — KONTAKTY SE PTAJI DAVKOU, NE PO JEDNOM.
 *
 * ROZHODNUTI UXR2-7 drzelo v seznamu `/klienti` dotaz na kazdy radek s tim,
 * ze „davkovy endpoint na kontakty neexistuje". Uz existuje
 * (`GET /v1/parties/contacts`), takze dvacet dotazu jedne stranky seznamu je
 * JEDEN dotaz. Podpis se nemeni — davku sklada `createIdBatcher` ze volani
 * v temze ticku, takze zadna obrazovka nemusi vedet, ze se davkuje.
 */
const contactsByParty = createIdBatcher<PartyContact[]>(async (partyIds) => {
  const result = await unwrap(
    apiClient.GET('/v1/parties/contacts', { params: { query: { partyIds: partyIds.join(',') } } }),
  );
  return new Map(result.items.map((item) => [item.partyId, item.contacts]));
}, () => []);

export async function listPartyContacts(id: string): Promise<PartyContact[]> {
  return contactsByParty(id);
}

/** Kolik nalezenych stran se jeste overuje kontaktem (hledani matchuje i jmeno). */
const EMAIL_MATCH_CANDIDATES = 5;

/**
 * Krok P8F6 bod 2 — KLIENT, KTERY UZ MA TENTYZ E-MAIL.
 *
 * Hleda se EXISTUJICIM `GET /v1/parties?q=` (server tam uz porovnava
 * normalizovanou hodnotu kontaktu, `findParties`), shoda se ale POTVRZUJE
 * kontakty strany: `q` matchuje i jmeno a „stejne jmeno" neni „stejny
 * e-mail". Vraci nejvys JEDNU aktivni stranu — nabidnout ji nebo zalozit
 * noveho klienta rozhoduje makler, nic se neslucuje samo.
 */
export async function findPartyByEmail(email: string): Promise<PartyDto | null> {
  const wanted = email.trim().toLowerCase();
  if (wanted === '') return null;
  const page = await listParties({ q: wanted, limit: EMAIL_MATCH_CANDIDATES });
  for (const party of page.items) {
    if (party.status !== 'active') continue;
    const contacts = await listPartyContacts(party.id);
    if (contacts.some((contact) => contact.kind === 'email' && contact.value.trim().toLowerCase() === wanted)) {
      return party;
    }
  }
  return null;
}

/** Zalozi klienta (osobu/organizaci). Vyzaduje Idempotency-Key (viz `plugin.ts registerIdempotentPost`). */
export async function createParty(body: CreatePartyBody): Promise<CreatePartyResponse> {
  return unwrap(
    apiClient.POST('/v1/parties', {
      params: { header: idempotencyKey(crypto.randomUUID()) },
      body,
    }),
  );
}

export async function addPartyContact(partyId: string, body: AddContactBody): Promise<PartyContact> {
  return unwrap(apiClient.POST('/v1/parties/{id}/contacts', { params: { path: { id: partyId } }, body }));
}

/** Krok PROP7 bod 2 — oprava jmena osoby v adresari (audit before/after). */
export async function updateParty(partyId: string, body: UpdatePartyBody): Promise<PartyDto> {
  return unwrap(apiClient.PATCH('/v1/parties/{id}', { params: { path: { id: partyId } }, body }));
}

/**
 * Krok PROP7 bod 2 — oprava kontaktu v adresari. Opravuje se TYZ radek
 * (`contactId`), ne pridava druhy: smlouva a poptavka ctou jeden telefon.
 */
export async function updatePartyContact(
  partyId: string,
  contactId: string,
  body: UpdateContactBody,
): Promise<PartyContact> {
  return unwrap(
    apiClient.PATCH('/v1/parties/{id}/contacts/{contactId}', {
      params: { path: { id: partyId, contactId } },
      body,
    }),
  );
}

export async function deletePartyContact(partyId: string, contactId: string): Promise<void> {
  await unwrap(
    apiClient.DELETE('/v1/parties/{id}/contacts/{contactId}', {
      params: { path: { id: partyId, contactId } },
    }),
  );
}

/**
 * Krok PROP7 bod 2 — odstraneni osoby z adresare. `linkedTo` prazdne =
 * smazano, neprazdne = archivovano, protoze osoba na necem visi.
 */
export async function archiveParty(partyId: string): Promise<ArchivePartyResult> {
  return unwrap(apiClient.DELETE('/v1/parties/{id}', { params: { path: { id: partyId } } }));
}

export async function addPartyTaxIdentifier(partyId: string, body: AddTaxIdentifierBody): Promise<unknown> {
  return unwrap(apiClient.POST('/v1/parties/{id}/tax-identifiers', { params: { path: { id: partyId } }, body }));
}
